import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import DateField from './DateField';

export interface SterilizationEntry {
  chip_id: string;
  sex: string;      // 'M' | 'F'
  noChip: boolean;  // operator explicitly declared the chip unavailable
}

export const emptyEntry = (): SterilizationEntry => ({ chip_id: '', sex: '', noChip: false });

/**
 * Validate the entries. Returns an i18n key for the first problem found, or null.
 * The sex is always required: it decides the recovery days (2 males, 3 females),
 * which are billed to the municipality.
 */
export const validateSterilizations = (entries: SterilizationEntry[]): string | null => {
  if (entries.length === 0) return 'sterilizationBatch.atLeastOne';
  for (const e of entries) {
    if (!e.noChip && !e.chip_id.trim()) return 'sterilizationBatch.chipRequired';
    if (!e.noChip && !/^\d+$/.test(e.chip_id.trim())) return 'sterilizationBatch.chipNumeric';
    if (e.sex !== 'M' && e.sex !== 'F') return 'sterilizationBatch.sexRequired';
  }
  const chips = entries.filter((e) => !e.noChip).map((e) => e.chip_id.trim());
  if (new Set(chips).size !== chips.length) return 'sterilizationBatch.duplicateChip';
  return null;
};

/** Shape expected by POST /api/municipality-expenses. */
export const toPayload = (entries: SterilizationEntry[]) =>
  entries.map((e) => ({ chip_id: e.noChip ? null : e.chip_id.trim(), sex: e.sex }));

interface Props {
  enabled: boolean;
  onEnabledChange: (v: boolean) => void;
  entries: SterilizationEntry[];
  onEntriesChange: (entries: SterilizationEntry[]) => void;
  procedureDate: string;
  onProcedureDateChange: (v: string) => void;
  /** True when the municipality refuses the post-sterilization recovery. */
  recoveryWaived: boolean;
  onRecoveryWaivedChange: (v: boolean) => void;
}

const SterilizationEntries: React.FC<Props> = ({
  enabled, onEnabledChange, entries, onEntriesChange, procedureDate, onProcedureDateChange,
  recoveryWaived, onRecoveryWaivedChange,
}) => {
  const { t } = useTranslation();

  const update = (i: number, patch: Partial<SterilizationEntry>) => {
    onEntriesChange(entries.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  };

  return (
    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 1.5 }}>
      <FormControlLabel
        control={(
          <Checkbox
            checked={enabled}
            onChange={(e) => {
              onEnabledChange(e.target.checked);
              if (e.target.checked && entries.length === 0) onEntriesChange([emptyEntry()]);
            }}
          />
        )}
        label={<Typography sx={{ fontWeight: 600 }}>{t('sterilizationBatch.containsSterilizations')}</Typography>}
      />

      {enabled && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          <Alert severity="info">{t('sterilizationBatch.explain')}</Alert>

          <DateField
            label={t('sterilizationBatch.procedureDate')}
            value={procedureDate}
            onChange={onProcedureDateChange}
          />

          {entries.map((entry, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label={t('external.chip')}
                value={entry.chip_id}
                onChange={(e) => update(i, { chip_id: e.target.value })}
                disabled={entry.noChip}
                size="small"
                inputProps={{ inputMode: 'numeric' }}
                sx={{ flex: 2 }}
              />
              <FormControl size="small" sx={{ flex: 1, minWidth: 90 }} required>
                <InputLabel>{t('external.sex')}</InputLabel>
                <Select
                  label={t('external.sex')}
                  value={entry.sex}
                  onChange={(e) => update(i, { sex: e.target.value })}
                >
                  <MenuItem value="M">{t('sterilizationBatch.maleDays')}</MenuItem>
                  <MenuItem value="F">{t('sterilizationBatch.femaleDays')}</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                sx={{ flex: 1.4, mr: 0 }}
                control={(
                  <Checkbox
                    size="small"
                    checked={entry.noChip}
                    onChange={(e) => update(i, { noChip: e.target.checked, chip_id: '' })}
                  />
                )}
                label={<Typography variant="caption">{t('sterilizationBatch.chipUnavailable')}</Typography>}
              />
              <IconButton
                size="small"
                color="error"
                disabled={entries.length === 1}
                onClick={() => onEntriesChange(entries.filter((_, idx) => idx !== i))}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => onEntriesChange([...entries, emptyEntry()])}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {t('sterilizationBatch.addCat')}
            </Button>
            <Typography variant="caption" color="text.secondary">
              {t('sterilizationBatch.count', { count: entries.length })}
            </Typography>
          </Box>

          <FormControlLabel
            control={(
              <Checkbox
                checked={!recoveryWaived}
                onChange={(e) => onRecoveryWaivedChange(!e.target.checked)}
              />
            )}
            label={(
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('sterilizationBatch.recoveryRecognised')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('sterilizationBatch.recoveryRecognisedHint')}
                </Typography>
              </Box>
            )}
          />

          <Alert severity="warning">{t('sterilizationBatch.chipUnavailableHint')}</Alert>
        </Box>
      )}
    </Box>
  );
};

export default SterilizationEntries;
